import React, { useEffect, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, X, Check, Megaphone } from "lucide-react";

export default function PromotersPage() {
  const { hasPerm } = useAuth();
  const [promoters, setPromoters] = useState([]);
  const [stats, setStats] = useState({}); // id -> stats
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);

  const load = async () => {
    try {
      const { data } = await api.get("/promoters");
      setPromoters(data);
      const entries = await Promise.all(data.map(async (p) => {
        try { const r = await api.get(`/promoters/${p.id}/stats`); return [p.id, r.data]; }
        catch (_) { return [p.id, null]; }
      }));
      setStats(Object.fromEntries(entries));
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/promoters/${editing.id}`, {
          name: form.name,
          contact_email: form.contact_email || null,
          contact_phone: form.contact_phone,
          commission_rate_pct: parseFloat(form.commission_rate_pct) || 0,
          notes: form.notes,
        });
        toast.success("Promoter updated");
      } else {
        await api.post("/promoters", {
          ...form,
          contact_email: form.contact_email || null,
          commission_rate_pct: parseFloat(form.commission_rate_pct) || 0,
        });
        toast.success("Promoter added");
      }
      setCreating(false); setEditing(null); setForm(BLANK); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, code: p.code,
      contact_email: p.contact_email || "", contact_phone: p.contact_phone || "",
      commission_rate_pct: p.commission_rate_pct, notes: p.notes || "",
    });
    setCreating(true);
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/promoters/${p.id}`, { active: !p.active });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const totalRevenue = promoters.reduce((s, p) => s + (stats[p.id]?.ticket_revenue || 0), 0);
  const totalHeads = promoters.reduce((s, p) => s + (stats[p.id]?.guests_arrived_heads || 0), 0);

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="promoters-page">
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">Events · Promoters</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Promoter roster</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Give each promoter their own code and commission rate. Attribution flows automatically from ticket sales and guest-list arrivals to the commission report on each event.
          </p>
        </div>
        {hasPerm("event.manage") && (
          <button onClick={() => { setEditing(null); setForm(BLANK); setCreating(true); }}
                  data-testid="promoter-new-btn"
                  className="btn-pill btn-pill-solid h-10">
            <Plus size={14}/> Add promoter
          </button>
        )}
      </div>

      <div className="mt-14 grid grid-cols-3 gap-x-12 gap-y-6 animate-fade-up animate-fade-up-1">
        <Stat num=".01" label="Active roster" value={promoters.filter((p) => p.active).length}/>
        <Stat num=".02" label="Ticket revenue (all-time)" value={peso(totalRevenue)}/>
        <Stat num=".03" label="Guests brought (heads)" value={totalHeads} accent/>
      </div>

      <div className="mt-10 border-y hairline-soft divide-y hairline-soft animate-fade-up animate-fade-up-2" data-testid="promoter-table">
        <div className="grid grid-cols-12 py-3 tiny-label ink-subtle">
          <div className="col-span-3">Promoter</div>
          <div className="col-span-2">Code</div>
          <div className="col-span-1 text-right">Rate</div>
          <div className="col-span-1 text-right">Events</div>
          <div className="col-span-1 text-right">Tickets</div>
          <div className="col-span-2 text-right">Revenue</div>
          <div className="col-span-1 text-right">Guests</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        {promoters.length === 0 && (
          <div className="py-8 ink-subtle text-center text-sm">No promoters on the roster yet.</div>
        )}
        {promoters.map((p) => {
          const s = stats[p.id] || {};
          return (
            <button key={p.id} onClick={() => openEdit(p)}
                    data-testid={`promoter-row-${p.id}`}
                    className="w-full text-left grid grid-cols-12 py-3 items-center hover:bg-foreground/[0.03] transition-colors">
              <div className="col-span-3 pr-2 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-foreground text-background flex items-center justify-center text-[10px] uppercase tracking-widest font-bold">{p.code.slice(0,3)}</div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{p.name}</div>
                  <div className="text-xs ink-subtle truncate">{p.contact_email || p.contact_phone || "—"}</div>
                </div>
              </div>
              <div className="col-span-2 mono text-sm ink">{p.code}</div>
              <div className="col-span-1 text-right mono text-sm ink">{p.commission_rate_pct}%</div>
              <div className="col-span-1 text-right mono text-sm ink">{s.events_touched ?? "—"}</div>
              <div className="col-span-1 text-right mono text-sm ink">{s.tickets_sold ?? "—"}</div>
              <div className="col-span-2 text-right mono text-sm ink">{peso(s.ticket_revenue ?? 0)}</div>
              <div className="col-span-1 text-right mono text-sm ink">{s.guests_arrived_heads ?? "—"}</div>
              <div className="col-span-1 text-right">
                {p.active
                  ? <span className="text-[9px] uppercase tracking-[0.22em] font-bold ink">ACTIVE</span>
                  : <span className="text-[9px] uppercase tracking-[0.22em] font-bold text-red-500">OFF</span>}
              </div>
            </button>
          );
        })}
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="promoter-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => { setCreating(false); setEditing(null); }}/>
          <form onSubmit={submit} className="relative w-full max-w-md surface-2 border-l hairline h-full p-6 overflow-y-auto shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <div className="section-eyebrow">Events · Promoters</div>
                <h2 className="mt-2 display-heading text-2xl ink">{editing ? "Edit promoter" : "Add promoter"}</h2>
              </div>
              <button type="button" onClick={() => { setCreating(false); setEditing(null); }} className="tiny-label ink-subtle hover:ink"><X size={16}/></button>
            </div>

            <F label="Name"><input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="i" data-testid="promoter-form-name"/></F>
            <F label="Code (unique)"><input required disabled={!!editing} value={form.code} onChange={(e)=>setForm({...form, code: e.target.value.toUpperCase()})} className="i mono" data-testid="promoter-form-code" placeholder="MEG"/></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Email"><input type="email" value={form.contact_email} onChange={(e)=>setForm({...form, contact_email: e.target.value})} className="i" data-testid="promoter-form-email"/></F>
              <F label="Phone"><input value={form.contact_phone} onChange={(e)=>setForm({...form, contact_phone: e.target.value})} className="i" data-testid="promoter-form-phone"/></F>
            </div>
            <F label="Commission rate (%)"><input required type="number" step="0.1" min="0" max="50" value={form.commission_rate_pct} onChange={(e)=>setForm({...form, commission_rate_pct: e.target.value})} className="i mono" data-testid="promoter-form-rate"/></F>
            <F label="Notes"><textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="i-tall" data-testid="promoter-form-notes"/></F>

            {editing && (
              <div className="mt-6">
                <button type="button" onClick={() => { toggleActive(editing); setCreating(false); setEditing(null); }}
                        className="text-[10px] uppercase tracking-[0.24em] font-bold text-red-500 hover:underline">
                  {editing.active ? "Deactivate promoter" : "Reactivate promoter"}
                </button>
              </div>
            )}

            <div className="mt-6 flex gap-2">
              <button type="button" onClick={() => { setCreating(false); setEditing(null); }} className="btn-pill btn-pill-outline h-11 flex-1">Cancel</button>
              <button type="submit" data-testid="promoter-form-submit" className="btn-pill btn-pill-solid h-11 flex-1"><Megaphone size={13}/> {editing ? "Save" : "Add"}</button>
            </div>

            <style>{`.i { margin-top: 6px; height: 42px; width: 100%; border-radius: 8px; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); padding: 0 14px; color: hsl(var(--foreground)); font-size: 13px; }
                     .i:disabled { opacity: 0.5; }
                     .i-tall { margin-top: 6px; width: 100%; border-radius: 8px; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); padding: 10px 14px; color: hsl(var(--foreground)); font-size: 13px; }`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

const BLANK = { name: "", code: "", contact_email: "", contact_phone: "", commission_rate_pct: 8, notes: "" };

function Stat({ num, label, value, accent }) {
  return (
    <div data-testid={`promoter-stat-${label.toLowerCase().replace(/[^\w]+/g,"-")}`}>
      <div className="flex items-center justify-between">
        <div className="section-eyebrow">{label}</div>
        <span className="section-num">{num}</span>
      </div>
      <div className={`mt-3 mono ink text-2xl font-semibold ${accent ? "border-l-2 border-foreground pl-3" : ""}`}>{value}</div>
    </div>
  );
}

function F({ label, children }) {
  return <label className="block mt-4"><div className="tiny-label">{label}</div>{children}</label>;
}
